'use client';

import React, { useState, useId, Suspense } from 'react';
import Image from 'next/image';

import Header from '@/components/Header';
import Footer from '@/components/Footer';

const faqGroups = [
  {
    heading: 'Non-Fault Claims',
    items: [
      {
        question: 'Do I pay my excess in a non-fault claim?',
        answer:
          'Not if we handle it through our non-fault service. We recover the full cost, including your excess, from the at-fault driver\'s insurer. You pay nothing upfront.',
      },
      {
        question: 'Will I lose my No Claims Bonus?',
        answer:
          'No. Because the claim is made against the other driver\'s policy, not yours, your No Claims Bonus is completely protected.',
      },
      {
        question: 'Do you provide a courtesy car?',
        answer:
          'Yes — we provide a free courtesy car while yours is being repaired, so you\'re never without transport.',
      },
      {
        question: 'What if the other driver disputes fault?',
        answer:
          'Our claims partner handles this. They\'ll gather evidence (your photos, witness statements, police reports) and fight your corner. You\'re supported throughout.',
      },
      {
        question: 'Do I need to tell my own insurance company?',
        answer:
          'Your policy may require you to notify them of any accident, even if you\'re not claiming. Check your policy documents. But if you\'re not claiming through them, it often doesn\'t affect your premiums.',
      },
      {
        question: 'What if my car is written off?',
        answer:
          'Our claims partner helps you get the correct market value from the at-fault insurer. If their offer seems low, they\'ll negotiate for you.',
      },
      {
        question: 'What if the accident was partially my fault?',
        answer:
          'Contact us anyway. We can advise you based on the circumstances. Some accidents that feel like your fault actually aren\'t — roundabout rules, parking incidents, and reversing situations can be complex.',
      },
      {
        question: 'What information should I collect at the scene?',
        answer:
          'Photos of all vehicles, damage, number plates, and the scene. The other driver\'s name, address, phone, insurance details, and reg. Witness details. Police report reference if attended.',
      },
    ],
  },
  {
    heading: 'Our Repairs & Services',
    items: [
      {
        question: 'Can you fix structural damage?',
        answer:
          'Yes. We have a Car-O-Liner precision bench that allows us to restore chassis and structural alignment to exact manufacturer specifications.',
      },
      {
        question: 'Do you code Mercedes and BMW modules?',
        answer:
          'Yes. We have dealer-level diagnostic and programming tools for all German manufacturers — Mercedes, BMW, Audi, Volkswagen, Porsche, and more.',
      },
      {
        question: 'How long do repairs take?',
        answer:
          'It depends on the extent of damage. We\'ll give you a clear timeline during the initial assessment and keep you updated throughout the repair.',
      },
    ],
  },
];

interface FAQItem {
  question: string;
  answer: string;
}

function AccordionItem({ question, answer }: FAQItem) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const buttonId = `faq-btn-${id}`;
  const panelId = `faq-panel-${id}`;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen((prev) => !prev);
    }
  };

  return (
    <div className="border border-white/[0.08] bg-[#111111] hover:border-white/[0.15] transition-colors duration-300">
      <button
        id={buttonId}
        onClick={() => setOpen(!open)}
        onKeyDown={handleKeyDown}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left min-h-[48px]"
        aria-expanded={open}
        aria-controls={panelId}
      >
        <span className="font-fraunces text-white text-lg font-light leading-snug">{question}</span>
        <span
          className={`flex-shrink-0 w-8 h-8 rounded-full border border-[#CC1A1A] flex items-center justify-center transition-transform duration-300 ${
            open ? 'rotate-45' : 'rotate-0'
          }`}
          aria-hidden="true"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M7 1v12M1 7h12" stroke="#CC1A1A" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </span>
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          open ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <p className="font-dm-sans text-white/60 text-base leading-relaxed px-6 pb-6">{answer}</p>
      </div>
    </div>
  );
}

function FAQAccordionSkeleton() {
  return (
    <div
      className="w-full py-24 bg-[#111111]"
      aria-hidden="true"
      style={{
        background: 'linear-gradient(90deg, #111111 25%, #1a1a1a 50%, #111111 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.5s infinite linear',
      }}
    />
  );
}

function FAQAccordionContent() {
  return (
    <div className="max-w-3xl mx-auto space-y-16">
      {/* FAQ Icons */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pb-8 border-b border-white/[0.06]">
        {[
          { src: '/assets/images/faq_icon_excess.png', alt: 'Glass shield protecting wallet representing zero excess on non-fault claims', label: 'Zero Excess' },
          { src: '/assets/images/faq_icon_ncb.png', alt: 'Trophy with glass shield representing No Claims Bonus protection', label: 'NCB Protected' },
          { src: '/assets/images/faq_icon_courtesy_car.png', alt: 'Courtesy car with free badge representing complimentary vehicle during repairs', label: 'Free Courtesy Car' },
          { src: '/assets/images/faq_icon_response.png', alt: 'Glass clock with checkmark representing fast response time', label: 'Fast Response' },
        ].map((icon) => (
          <div key={icon.label} className="flex flex-col items-center gap-3">
            <div className="w-16 h-16 relative">
              <Image src={icon.src} alt={icon.alt} width={64} height={64} className="w-full h-full object-contain" />
            </div>
            <span className="font-dm-sans text-xs text-white/50 text-center tracking-wide">{icon.label}</span>
          </div>
        ))}
      </div>

      {faqGroups.map((group) => (
        <div key={group.heading}>
          <div className="flex items-center gap-4 mb-8" aria-hidden="true">
            <span className="w-8 h-px bg-[#CC1A1A]" />
            <h2 className="font-fraunces text-2xl font-light text-white tracking-wide">
              {group.heading}
            </h2>
            <span className="flex-1 h-px bg-white/[0.06]" />
          </div>
          <div className="space-y-2" role="list" aria-label={`${group.heading} questions`}>
            {group.items.map((item) => (
              <div key={item.question} role="listitem">
                <AccordionItem question={item.question} answer={item.answer} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function FAQPage() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Header />
      <main id="main-content">

        {/* Hero Section */}
        <section className="pt-32 pb-20 px-6 relative overflow-hidden" aria-labelledby="faq-hero-heading">
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
              backgroundSize: '60px 60px',
            }}
            aria-hidden="true"
          />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#CC1A1A]/10 blur-[120px] rounded-full" aria-hidden="true" />

          <div className="max-w-4xl mx-auto text-center relative z-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#CC1A1A]/30 bg-[#CC1A1A]/10 mb-8" aria-hidden="true">
              <span className="w-1.5 h-1.5 rounded-full bg-[#CC1A1A] animate-pulse" />
              <span className="font-dm-sans text-[#CC1A1A] text-xs font-bold tracking-[0.2em] uppercase">
                GOT QUESTIONS?
              </span>
            </div>

            <h1 id="faq-hero-heading" className="font-fraunces text-5xl md:text-6xl font-light text-white leading-tight mb-6">
              Frequently Asked
              <br />
              <span className="text-[#CC1A1A]">Questions</span>
            </h1>

            <p className="font-dm-sans text-white/50 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto">
              Everything you need to know about non-fault claims, our repairs, and how we work.
            </p>
          </div>
        </section>

        {/* FAQ Accordion Section — lazy loaded */}
        <section className="pb-24 px-6" aria-labelledby="faq-accordion-heading">
          <h2 id="faq-accordion-heading" className="sr-only">FAQ Accordion</h2>
          <Suspense fallback={<FAQAccordionSkeleton />}>
            <FAQAccordionContent />
          </Suspense>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-6 bg-[#0d0d0d] border-t border-white/[0.06] relative overflow-hidden" aria-labelledby="faq-cta-heading">
          <div className="absolute inset-0 bg-gradient-to-br from-[#CC1A1A]/5 via-transparent to-transparent" aria-hidden="true" />
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <p className="font-dm-sans text-[#CC1A1A] text-xs font-bold tracking-[0.25em] uppercase mb-4" aria-hidden="true">
              NEED MORE HELP?
            </p>
            <h2 id="faq-cta-heading" className="font-fraunces text-4xl md:text-5xl font-light text-white mb-4">
              Still have questions?
            </h2>
            <p className="font-dm-sans text-white/50 text-lg mb-10">
              Get in touch — we&apos;re happy to talk you through anything.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="mailto:contactus@nolimitauto.co.uk"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#CC1A1A] text-white font-dm-sans font-bold text-sm uppercase tracking-widest hover:bg-[#E02020] transition-all duration-300 min-h-[44px]"
              >
                Contact Us
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </a>
              <a
                href="tel:01614001687"
                className="inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-white font-dm-sans font-medium text-sm hover:border-white/40 hover:bg-white/5 transition-all duration-300 min-h-[44px]"
                aria-label="Call us on 0161 400 1687"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012 .18h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z"/>
                </svg>
                0161 400 1687
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
